import { Box, HStack, useColorMode } from '@chakra-ui/react'
import { useFlags } from '@missionlabs/feature-flags'
import { Button, Input, useUpdateParams } from '@missionlabs/react'
import { AddCircleIcon, SearchIcon } from '@missionlabs/react/circleloop'
import { FC, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate, useNavigate } from 'react-router-dom'
import { SettingsPage } from 'shared/components/settings/SettingsPage'
import { Features, useHasClientFeature } from 'shared/hooks/useHasClientFeature'
import { useGetNumbersQuery } from 'shared/store'
import { isPhoneLinePlus } from 'shared/utils/env'

import { NumbersTable } from './NumbersTable'

export const Numbers: FC = () => {
    const navigate = useNavigate()
    const { t } = useTranslation()
    const { colorMode } = useColorMode()

    const flags = useFlags(['admin_numbers_add_remove', 'admin_numbers_port_mirroring'])

    // Ensure we have either porting or mirroring enabled to show button
    let defaultExistingPath: string | undefined = undefined
    const isMirroringClientEnabled = useHasClientFeature(Features.mirroring)
    const isPortingClientEnabled = useHasClientFeature(Features.porting)
    if (isPortingClientEnabled) defaultExistingPath = 'port'
    else if (isMirroringClientEnabled) {
        defaultExistingPath = isPhoneLinePlus ? 'present' : 'mirror'
    }

    const { data: numbers = [], isLoading } = useGetNumbersQuery({
        loadAssociations: true
    })

    const isExistingNumberEnabled = useMemo(
        () => flags.admin_numbers_port_mirroring.enabled && defaultExistingPath,
        [defaultExistingPath, flags.admin_numbers_port_mirroring.enabled]
    )

    const [searchParams, updateParams] = useUpdateParams()
    const search = searchParams.get('search') || ''
    const setSearch = (value: string) => updateParams({ search: value })

    // Redirect to number edit page if numberE164 is in the URL.
    // This is useful for deep linking to a specific number when we don't have the number ID,
    // i.e. from the edit user/team/menu drawer, where phoneNumbers is just a string[]
    if (searchParams.get('numberE164') && numbers.length > 0) {
        const number = numbers.find(n => n.numberE164 === searchParams.get('numberE164'))
        if (number) {
            return <Navigate to={`/admin/numbers/${number.ID}/edit`} replace={true} />
        }
    }

    return (
        <SettingsPage
            maxW="100%"
            title={t('admin.numbers.title')}
            subtitle={t('admin.numbers.subheading')}
            isLoading={isLoading}
            isEmpty={!numbers.length}
            actions={
                <HStack spacing="8px">
                    {isExistingNumberEnabled && (
                        <Button
                            variant="secondary"
                            leftIcon={<AddCircleIcon boxSize="16px" />}
                            onClick={() =>
                                navigate(`/admin/numbers/existing/${defaultExistingPath}`)
                            }
                        >
                            {isPhoneLinePlus
                                ? t('admin.numbers.addPresentationNumber')
                                : t('admin.numbers.addExistingNumber')}
                        </Button>
                    )}
                    {flags.admin_numbers_add_remove.enabled && (
                        <Button
                            variant="creationary"
                            leftIcon={<AddCircleIcon boxSize="16px" />}
                            onClick={() => navigate('/admin/numbers/add')}
                        >
                            {t('admin.numbers.addNumber')}
                        </Button>
                    )}
                </HStack>
            }
        >
            <Box w="50%">
                <Input
                    value={search}
                    onChange={e => setSearch(e.currentTarget.value)}
                    placeholder={t('admin.numbers.search_placeholder')}
                    rightIcon={<SearchIcon boxSize="20px" color={`${colorMode}.coolGrey.60`} />}
                />
            </Box>
            <NumbersTable numbers={numbers} searchTerm={search} />
        </SettingsPage>
    )
}
